import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import useApiCallService from "../../Common/Helpers/ApiCall";
import SortableList from "../../Common/Components/Lists/SortableList";
import { Button, Col, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import {
    ADMIN_PATHS,
    USER_PATHS,
    REPORT_PROCEEDING_PATHS,
    ORGANIZATION_PATHS,
    PAY_PATHS,
    SUPER_ADMIN_PATHS,
} from "../../config";
import GuideModel from "../Models/GuideModel";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { ArticleForm } from "./HelpPanel";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { ChangeDateFormat } from "../../Common/Helpers/ChangeDateFormat";

const normalizeOrders = (arr) => arr.map((a, i) => ({ ...a, order: i + 1 }));
const isContentEmpty  = (html) => !html || html.trim() === '' || html === '<p><br></p>';
const toNodeIdList = (nodes) =>
    (nodes ?? [])
        .map((node) => node?.id)
        .filter((id) => id != null)
        .map((id) => String(id));

const reorderNodesByIds = (nodes = [], orderedIds = []) => {
    if (!orderedIds?.length) return nodes;

    const nodeById = new Map((nodes ?? []).map((node) => [String(node?.id), node]));
    const ordered = orderedIds.map((id) => nodeById.get(String(id))).filter(Boolean);
    const orderedSet = new Set((orderedIds ?? []).map((id) => String(id)));
    const remaining = (nodes ?? []).filter((node) => !orderedSet.has(String(node?.id)));

    return [...ordered, ...remaining];
};

const GUIDE_SECTION_TITLES = {
    user: t("modelLabel.forYou") || 'For You',
    organization: t("modelLabel.forYourOrganization") || 'For Your Organization',
    admin: t("adminLabel.admin") || 'Admin',
    superAdmin: t("adminLabel.system") || 'System',
    unsectioned: t("guide.unsectioned") || 'Sin sección',
};

const ORGANIZATION_COLLECT_ROUTE_PREFIXES = [
    '/pay-quickpayment',
    '/qr-code-list',
    '/invoice-list',
];

const ADMIN_REPORTS_ROUTE_PREFIXES = [
    '/admin/report-tax',
    '/admin/report-tax-info',
    '/admin/export-sie',
    '/admin/salary-sheet',
    '/admin/economic-report',
    '/admin/economic-report-info',
    '/admin/swedbank-report',
];

const SUPER_ADMIN_FROM_ADMIN_ROUTE_PREFIXES = [
    '/admin/organization-list',
    '/admin/user-list',
    '/admin/role',
    '/admin/cost-bearer',
    '/admin/guide-list',
    '/admin/accounting-list',
    '/admin/accounting',
];

const SUPER_ADMIN_SETTINGS_ROUTE_PREFIXES = [
    '/admin/role',
    '/admin/cost-bearer',
    '/admin/guide-list',
    '/admin/accounting-list',
    '/admin/accounting',
];

const GUIDE_PATH_SECTION_MAP = (() => {
    const map = new Map();

    Object.values(USER_PATHS).forEach((path) => map.set(path, 'user'));
    Object.values(REPORT_PROCEEDING_PATHS).forEach((path) => map.set(path, 'user'));
    Object.values(ORGANIZATION_PATHS).forEach((path) => map.set(path, 'organization'));
    Object.values(PAY_PATHS).forEach((path) => map.set(path, 'organization'));
    Object.values(ADMIN_PATHS).forEach((path) => map.set(path, 'admin'));
    Object.values(SUPER_ADMIN_PATHS).forEach((path) => map.set(path, 'superAdmin'));

    return map;
})();

function getGuideSectionMeta(routeId) {
    if (!routeId) {
        return {
            key: 'unsectioned',
            title: GUIDE_SECTION_TITLES.unsectioned ?? 'Sin sección',
        };
    }

    const forcedSuperAdmin = SUPER_ADMIN_FROM_ADMIN_ROUTE_PREFIXES.some(
        (prefix) => routeId === prefix || routeId.startsWith(`${prefix}/`)
    );
    if (forcedSuperAdmin) {
        return {
            key: 'superAdmin',
            title: GUIDE_SECTION_TITLES.superAdmin ?? 'System',
        };
    }

    const sectionKey = GUIDE_PATH_SECTION_MAP.get(routeId);
    if (!sectionKey) {
        return {
            key: 'unsectioned',
            title: GUIDE_SECTION_TITLES.unsectioned ?? 'Sin sección',
        };
    }
    return {
        key: sectionKey,
        title: GUIDE_SECTION_TITLES[sectionKey] ?? sectionKey,
    };
}

function isGuideVisibleBySection(guide, sectionExpandedMap) {
    const sectionMeta = getGuideSectionMeta(guide?.routeId);
    if (!sectionMeta) return true;
    return !!sectionExpandedMap?.[sectionMeta.key];
}

function hasVisibleNodeBySection(node, sectionExpandedMap) {
    if (node?.type === 'guide') {
        const selfVisible = isGuideVisibleBySection(node.guide, sectionExpandedMap);
        const childVisible = (node.childGuides ?? []).some((child) => hasVisibleNodeBySection(child, sectionExpandedMap));
        return selfVisible || childVisible;
    }

    const children = node?.children ?? [];
    return children.some((child) => hasVisibleNodeBySection(child, sectionExpandedMap));
}

function isOrganizationCollectRoute(routeId) {
    if (!routeId) return false;
    return ORGANIZATION_COLLECT_ROUTE_PREFIXES.some(
        (prefix) => routeId === prefix || routeId.startsWith(`${prefix}/`)
    );
}

function hasOrganizationCollectRoute(node) {
    if (!node) return false;

    if (node?.type === 'guide') {
        if (isOrganizationCollectRoute(node?.guide?.routeId)) return true;
        return (node.childGuides ?? []).some((child) => hasOrganizationCollectRoute(child));
    }

    return (node?.children ?? []).some((child) => hasOrganizationCollectRoute(child));
}

function isAdminReportsRoute(routeId) {
    if (!routeId) return false;
    return ADMIN_REPORTS_ROUTE_PREFIXES.some(
        (prefix) => routeId === prefix || routeId.startsWith(`${prefix}/`)
    );
}

function hasAdminReportsRoute(node) {
    if (!node) return false;

    if (node?.type === 'guide') {
        if (isAdminReportsRoute(node?.guide?.routeId)) return true;
        return (node.childGuides ?? []).some((child) => hasAdminReportsRoute(child));
    }

    return (node?.children ?? []).some((child) => hasAdminReportsRoute(child));
}

function isSuperAdminSettingsRoute(routeId) {
    if (!routeId) return false;
    return SUPER_ADMIN_SETTINGS_ROUTE_PREFIXES.some(
        (prefix) => routeId === prefix || routeId.startsWith(`${prefix}/`)
    );
}

function hasSuperAdminSettingsRoute(node) {
    if (!node) return false;

    if (node?.type === 'guide') {
        if (isSuperAdminSettingsRoute(node?.guide?.routeId)) return true;
        return (node.childGuides ?? []).some((child) => hasSuperAdminSettingsRoute(child));
    }

    return (node?.children ?? []).some((child) => hasSuperAdminSettingsRoute(child));
}

function filterNodesBySection(nodes, sectionKey) {
    const filtered = [];

    nodes.forEach((node) => {
        if (node?.type === 'guide') {
            const guideSectionKey = getGuideSectionMeta(node?.guide?.routeId)?.key;
            const filteredChildGuides = filterNodesBySection(node?.childGuides ?? [], sectionKey);
            if (guideSectionKey === sectionKey) {
                filtered.push({ ...node, childGuides: filteredChildGuides });
                return;
            }

            if (filteredChildGuides.length > 0) {
                filtered.push(...filteredChildGuides);
            }
            return;
        }

        const children = filterNodesBySection(node?.children ?? [], sectionKey);
        if (children.length > 0) {
            filtered.push({ ...node, children });
        }
    });

    return filtered;
}

function nestGuidesByParent(nodes) {
    const entries = [];

    const walk = (arr) => {
        arr.forEach((node) => {
            if (node?.type === 'guide') {
                entries.push({ node, siblings: arr });
                return;
            }
            walk(node?.children ?? []);
        });
    };

    walk(nodes);

    const byId = new Map();
    entries.forEach((entry) => {
        if (entry?.node?.guide?.id != null) {
            byId.set(String(entry.node.guide.id), entry);
        }
    });

    const wouldCreateCycle = (childId, parentId) => {
        let currentId = parentId == null ? null : String(parentId);
        const seen = new Set();

        while (currentId) {
            if (currentId === String(childId)) return true;
            if (seen.has(currentId)) return true;
            seen.add(currentId);

            const parentEntry = byId.get(currentId);
            const nextParentId = parentEntry?.node?.guide?.parentId;
            currentId = nextParentId == null ? null : String(nextParentId);
        }

        return false;
    };

    const removals = new Map();

    entries.forEach((entry) => {
        const parentId = entry?.node?.guide?.parentId;
        if (parentId == null) return;

        const parentEntry = byId.get(String(parentId));
        if (!parentEntry) return;

        const childGuideId = entry?.node?.guide?.id;
        if (childGuideId == null) return;
        if (String(parentEntry?.node?.guide?.id) === String(childGuideId)) return;
        if (wouldCreateCycle(childGuideId, parentId)) return;

        if (!parentEntry.node.childGuides) {
            parentEntry.node.childGuides = [];
        }
        parentEntry.node.childGuides.push(entry.node);

        if (!removals.has(entry.siblings)) {
            removals.set(entry.siblings, new Set());
        }
        removals.get(entry.siblings).add(entry.node);
    });

    removals.forEach((nodesToRemove, siblings) => {
        for (let i = siblings.length - 1; i >= 0; i -= 1) {
            if (nodesToRemove.has(siblings[i])) {
                siblings.splice(i, 1);
            }
        }
    });

    const sortGuideChildren = (arr) => {
        arr.forEach((node) => {
            if (node?.type === 'guide' && Array.isArray(node.childGuides) && node.childGuides.length > 0) {
                node.childGuides.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
                sortGuideChildren(node.childGuides);
            }
            if (node?.type === 'container') {
                sortGuideChildren(node.children ?? []);
            }
        });
    };

    sortGuideChildren(nodes);
    return nodes;
}

function getTouchDropTarget(clientX, clientY) {
    const elem = document.elementFromPoint(clientX, clientY);
    if (!elem) return null;

    const container = elem.closest?.('[data-container-drop-id]');
    if (container) {
        return {
            type: 'container',
            containerId: container.getAttribute('data-container-drop-id'),
            rootIndex: null,
        };
    }

    const root = elem.closest?.('[data-root-drop-zone]');
    if (root) {
        const raw = root.getAttribute('data-root-drop-zone');
        const parsed = Number(raw);
        return {
            type: 'root',
            containerId: null,
            rootIndex: Number.isNaN(parsed) ? null : parsed,
        };
    }

    return null;
}

// ── Builds a filtered tree ────────────────────────────────────────────────────
function buildFilteredTree(containers, guideItems, forcedRootGuideIds = [], forcedParentByGuideId = {}) {
    const forcedRootSet = new Set((forcedRootGuideIds ?? []).map((id) => String(id)));
    const forcedParentMap = new Map(
        Object.entries(forcedParentByGuideId ?? {}).map(([guideId, parentId]) => [String(guideId), parentId])
    );

    const normalizeGuideParent = (guide) => {
        if (!guide) return guide;
        const guideId = String(guide?.id);
        if (forcedParentMap.has(guideId)) {
            return { ...guide, parentId: forcedParentMap.get(guideId) };
        }
        if (forcedRootSet.has(guideId)) {
            return { ...guide, parentId: null };
        }
        return guide;
    };
    const guideMap = new Map();
    guideItems.forEach((guide) => {
        if (guide?.id != null) guideMap.set(String(guide.id), guide);
        if (guide?.routeId) guideMap.set(String(guide.routeId), guide);
    });

    function processNodes(nodes) {
        const result = [];
        for (const node of nodes) {
            if (node.type === 'guide') {
                const guideNode = node?.guide ?? {};
                const keyById = guideNode.id != null ? String(guideNode.id) : null;
                const keyByRoute = guideNode.routeId ? String(guideNode.routeId) : null;
                const matchedGuide = (keyById && guideMap.get(keyById)) || (keyByRoute && guideMap.get(keyByRoute));

                if (matchedGuide) {
                    const normalizedGuide = normalizeGuideParent(matchedGuide);
                    result.push({ ...node, guide: normalizedGuide, id: normalizedGuide.id ?? node.id });
                }
            // If no match, skip this guide node entirely (don't include in tree)
            } else {
                const children = processNodes(node.children ?? []);
                result.push({ ...node, children });
            }
        }
        return result.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    const containerNodes = processNodes(containers);

    const containerGuideKeys = new Set();
    const collectIds = (nodes) => nodes.forEach(n => {
        if (n.type === 'guide') {
            if (n.guide?.id != null) containerGuideKeys.add(String(n.guide.id));
            if (n.guide?.routeId) containerGuideKeys.add(String(n.guide.routeId));
        }
        else collectIds(n.children ?? []);
    });
    collectIds(containerNodes);

    const rootGuides = guideItems
        .filter(g => !containerGuideKeys.has(String(g?.id)) && !containerGuideKeys.has(String(g?.routeId)))
        .map(g => {
            const normalizedGuide = normalizeGuideParent(g);
            return ({
            id: g.id,
            type: 'guide',
            name: g.routeId,
            order: g.order ?? 0,
            guide: normalizedGuide,
            });
        });

    const tree = [...containerNodes, ...rootGuides]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return nestGuidesByParent(tree);
}

// ── Builds the reorder-level payload from a tree array ────────────────────────
function buildReorderPayload(orderedNodes, parentContainerId) {
    return {
        items: orderedNodes
            .filter((node) => node?.type === 'guide')
            .map((node, i) => ({
                guideId: node?.guide?.id ?? node?.id,
                order: i + 1,
            })),
    };
}

// ── MoveOrCopyModal ───────────────────────────────────────────────────────────
function MoveOrCopyModal({ article, onMove, onCopy, onClose }) {
    const { t } = useTranslation();

    const modalBody = (
        <div>
            <h3 className="gc-modal-title">{t('guide.moveOrCopy') || 'Flytta eller kopiera?'}</h3>
            <p className="gc-modal-subtitle"><strong>{article.title}</strong></p>
        </div>
    );

    const modalFooter = (
        <>
            <Button className="box-green border-0" onClick={onMove}>
                <i className="bx bx-transfer" style={{ marginRight: 6 }} />
                {t('guide.move') || 'Flytta'}
            </Button>
            <Button className="box-green border-0" onClick={onCopy}>
                <i className="bx bx-copy" style={{ marginRight: 6 }} />
                {t('guide.copy') || 'Kopiera'}
            </Button>
            <Button className="box-red border-0" onClick={onClose}>{t('button.cancel')}</Button>
        </>
    );

    return (
        <CustomModal
            isOpen={true}
            toggle={onClose}
            body={modalBody}
            footer={modalFooter}
            size="md"
        />
    );
}

// ── MoveGuideModal ────────────────────────────────────────────────────────────
function MoveGuideModal({ guide, containers, onMove, onClose }) {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState('__root__');

    const options = [{ id: '__root__', label: t('guide.rootLevel') || '— Raíz —', depth: 0 }];
    const flatten = (nodes, depth = 0) => {
        nodes.forEach(node => {
            if (node.type === 'container') {
                options.push({ id: node.id, label: node.name, depth });
                flatten(node.children ?? [], depth + 1);
            }
        });
    };
    flatten(containers);

    return (
        <div className="gc-modal-overlay" onClick={onClose}>
            <div className="gc-modal" onClick={e => e.stopPropagation()}>
                <h3 className="gc-modal-title">{t('guide.moveGuide') || 'Mover guía a...'}</h3>
                <p className="gc-modal-subtitle"><strong>{guide.routeId}</strong></p>
                <select
                    className="hp-form-select"
                    style={{ width: '100%', marginBottom: 16 }}
                    value={selectedId}
                    onChange={e => setSelectedId(e.target.value)}
                >
                    {options.map(opt => (
                        <option key={opt.id} value={opt.id}>
                            {'　'.repeat(opt.depth)}{opt.label}
                        </option>
                    ))}
                </select>
                <div className="gc-modal-actions">
                    <button
                        className="gc-btn gc-btn--save"
                        onClick={() => onMove(selectedId === '__root__' ? null : selectedId)}
                    >
                        {t('guide.move') || 'Mover'}
                    </button>
                    <button className="gc-btn gc-btn--cancel" onClick={onClose}>{t('button.cancel')}</button>
                </div>
            </div>
        </div>
    );
}

// ── ContainerForm ─────────────────────────────────────────────────────────────
function ContainerForm({ initial = {}, onSave, onCancel, saving }) {
    const { t } = useTranslation();
    const [name, setName] = useState(initial.name ?? '');

    return (
        <div className="hp-form-wrapper">
            <div className="hp-form-topbar">
                <div className="hp-form-topbar-actions hp-form-topbar-actions--guide">
                    <button
                        className="hp-btn-save-top"
                        onClick={() => onSave({ name, parentId: initial.parentId ?? null })}
                        disabled={!name.trim() || saving}
                    >
                        {saving && <Spinner size="sm" color="light" />}
                        {t('button.save') || 'Spara'}
                    </button>
                    <button className="hp-btn-back-top" onClick={onCancel} disabled={saving}>
                        {t('button.back') || 'Tillbaka'}
                    </button>
                </div>
            </div>
            <div className="hp-form-field" style={{ padding: 16 }}>
                <label className="hp-form-label">{t('modelLabel.name') || 'Nombre'}</label>
                <input
                    className="hp-form-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={t('modelLabel.name') || 'Nombre del contenedor'}
                    autoFocus
                />
            </div>
        </div>
    );
}

function GuideForm({ initial = {}, onSave, onCancel, onDelete = null, saving, isEdit = false, canDelete = false }) {
    const { t } = useTranslation();
    const [routeId, setRouteId] = useState(initial.routeId ?? '');
    const [description, setDescription] = useState(initial.description ?? '');
    const [content, setContent] = useState(initial.content ?? '');

    useEffect(() => {
        setRouteId(initial.routeId ?? '');
        setDescription(initial.description ?? '');
        setContent(initial.content ?? '');
    }, [initial]);

    return (
        <div className="hp-form-wrapper">
            <div className="hp-form-topbar">
                <div className={`hp-form-topbar-actions${isEdit ? ' hp-form-topbar-actions--guide' : ''}`}>
                    <button
                        className="hp-btn-save-top"
                        onClick={() => onSave({ routeId, description, content })}
                        disabled={!routeId.trim() || saving}
                    >
                        {saving && <Spinner size="sm" color="light" />}
                        {isEdit ? (t('button.update') || 'Update') : (t('button.save') || 'Save')}
                    </button>
                    <button className="hp-btn-back-top" onClick={onCancel} disabled={saving}>
                        {t('button.back') || 'Back'}
                    </button>
                    {isEdit && canDelete && typeof onDelete === 'function' && (
                        <button className="hp-btn-delete-top" onClick={onDelete} disabled={saving}>
                            {t('button.delete') || 'Delete'}
                        </button>
                    )}
                </div>
            </div>
            <div className="hp-form-field" style={{ padding: 16 }}>
                <label className="hp-form-label">{t('guide.routeId', 'Route')}</label>
                <input
                    className="hp-form-input"
                    value={routeId}
                    onChange={(e) => setRouteId(e.target.value)}
                    placeholder={t('guide.routeId', 'Route')}
                    disabled={isEdit}
                    autoFocus
                />
            </div>
            <div className="hp-form-field" style={{ padding: '0 16px 16px' }}>
                <label className="hp-form-label">{t('guide.description', 'Description')}</label>
                <input
                    className="hp-form-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('guide.descriptionPlaceholder', 'Add a short description...')}
                />
            </div>
            <div className="hp-form-field" style={{ padding: '0 16px 16px' }}>
                <label className="hp-form-label">{t('guide.content', 'Content key')}</label>
                <input
                    className="hp-form-input"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={t('guide.content', 'Content key')}
                />
            </div>
        </div>
    );
}

// ── RootDropZone ──────────────────────────────────────────────────────────────
// Always in the DOM — never conditionally removed during a drag.
// Visibility is controlled purely by CSS/style based on the `over` hover state.
function RootDropZone({ insertIndex, treeRef, dragStateRef, onReorderLevel, onGuideDropToRoot, touchDropTarget, canDropContainers, dragVisualResetTick }) {
    const [over, setOver] = useState(false);
    const touchOver = touchDropTarget?.type === 'root' && touchDropTarget?.rootIndex === insertIndex;
    const isOver = over || touchOver;

    useEffect(() => {
        setOver(false);
    }, [dragVisualResetTick]);

    return (
        <div
            data-root-drop-zone={insertIndex}
            style={{
                height: isOver ? 32 : 8,
                borderRadius: 6,
                background: isOver ? '#e0e7ff' : 'transparent',
                border: isOver ? '2px dashed #6366f1' : '2px dashed transparent',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: '#6366f1',
                margin: '2px 0',
                // Always accepts drops — the handler checks if we're actually dragging
                pointerEvents: 'all',
            }}
            onDragOver={(e) => {
                if (!canDropContainers) return;
                const ds = dragStateRef.current;
                if (!ds.draggingGuide && !ds.draggingContainer) return;
                e.preventDefault();
                e.stopPropagation();
                setOver(true);
            }}
            onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOver(false);
            }}
            onDrop={(e) => {
                if (!canDropContainers) return;
                e.preventDefault();
                e.stopPropagation();
                setOver(false);

                const { draggingGuide, draggingContainer } = dragStateRef.current;
                const tree = treeRef.current;

                const draggedNode = draggingContainer
                    ? { type: 'container', id: draggingContainer.id, ...draggingContainer }
                    : draggingGuide
                        ? { type: 'guide', id: draggingGuide.guide.id, guide: draggingGuide.guide, order: draggingGuide.guide.order ?? 0 }
                        : null;

                if (!draggedNode) return;

                const withoutDragged = tree.filter(n => n.id !== draggedNode.id);
                const originalIndex  = tree.findIndex(n => n.id === draggedNode.id);
                const adjustedIndex  = (originalIndex >= 0 && insertIndex > originalIndex)
                    ? insertIndex - 1
                    : insertIndex;

                // If guide comes from a nested parent (not root), first detach it from parent.
                if (draggingGuide && originalIndex < 0) {
                    onGuideDropToRoot?.(draggingGuide.guide);
                    return;
                }

                // No-op drop: same resulting position, skip API/reload.
                if (originalIndex >= 0 && adjustedIndex === originalIndex) return;

                const newTree        = [...withoutDragged];
                newTree.splice(adjustedIndex, 0, draggedNode);

                onReorderLevel(newTree, null);
            }}
        >
            {isOver && <span>{t('modelLabel.dropHere')}</span>}
        </div>
    );
}

function GuideSiblingDropZone({ insertIndex, siblingGuides, dragStateRef, onReorderLevel, canDropContainers, parentGroupId, dragVisualResetTick }) {
    const { t } = useTranslation();
    const [over, setOver] = useState(false);

    useEffect(() => {
        setOver(false);
    }, [dragVisualResetTick]);

    return (
        <div
            style={{
                height: over ? 32 : 8,
                borderRadius: 6,
                background: over ? '#e0e7ff' : 'transparent',
                border: over ? '2px dashed #6366f1' : '2px dashed transparent',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: '#6366f1',
                margin: '2px 0',
                pointerEvents: 'all',
            }}
            onDragOver={(e) => {
                if (!canDropContainers) return;
                const ds = dragStateRef.current;
                if (!ds.draggingGuide) return;
                e.preventDefault();
                e.stopPropagation();
                setOver(true);
            }}
            onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOver(false);
            }}
            onDrop={(e) => {
                if (!canDropContainers) return;
                e.preventDefault();
                e.stopPropagation();
                setOver(false);

                const draggedGuide = dragStateRef.current.draggingGuide?.guide;
                if (!draggedGuide) return;

                const originalIndex = siblingGuides.findIndex(
                    (node) => String(node?.guide?.id) === String(draggedGuide.id)
                );

                // Only reorder within the same parent group here.
                if (originalIndex < 0) return;

                const withoutDragged = siblingGuides.filter(
                    (node) => String(node?.guide?.id) !== String(draggedGuide.id)
                );
                const adjustedIndex = insertIndex > originalIndex ? insertIndex - 1 : insertIndex;

                if (adjustedIndex === originalIndex) return;

                const draggedNode = siblingGuides[originalIndex];
                const newOrder = [...withoutDragged];
                newOrder.splice(adjustedIndex, 0, draggedNode);

                onReorderLevel?.(newOrder, parentGroupId);
            }}
        >
            {over && <span>{t('modelLabel.dropHere')}</span>}
        </div>
    );
}

// ── GuideCard ─────────────────────────────────────────────────────────────────
function GuideCard({
    guide, canEdit, canDelete,
    onDeleteGuide, onEditGuide, onNewArticle, onEditArticle, onMoveGuide,
    onGuideDropIntoGuide,
    childGuides,
    depth,
    handlers,
    dragStateRef,
    onArticleDragStart, onArticleDragEnd, onArticleDropFromOutside,
    onGuideDragStart, onGuideDragEnd, onGuideDrop,
    onGuideTouchStart, onGuideTouchMove, onGuideTouchEnd,
    canDragContainers,
    touchDragging,
    articleCountOverride,
    articleRefreshTick,
}) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const [expanded,          setExpanded]          = useState(false);
    const [articles,          setArticles]          = useState([]);
    const [loading,           setLoading]           = useState(false);
    const [reordering,        setReordering]        = useState(false);
    const [dragOver,          setDragOver]          = useState(null);
    const [expandedArticleId, setExpandedArticleId] = useState(null);
    const [displayDeleteArticleModal, setDisplayDeleteArticleModal] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [isExtArtDragOver,  setIsExtArtDragOver]  = useState(false);
    const [isGuideDropOver,   setIsGuideDropOver]   = useState(false);
    const [externalDropIndex, setExternalDropIndex] = useState(null);

    const dragIndexRef            = useRef(null);
    const isDraggingInternallyRef = useRef(false);
    const touchDragRef            = useRef({ active: false, sourceIndex: null, targetIndex: null, startY: 0, moved: false });

    const getIsExternalArticleDrag = () => {
        const ds = dragStateRef.current;
        return !!(ds.draggingArticle && ds.draggingArticle.fromRouteId !== guide.routeId);
    };

    const loadArticles = useCallback(() => {
        setLoading(true);
        ApiCall(
            { method: 'get', url: `/guide/by-route?routeId=${encodeURIComponent(guide.routeId)}` },
            (res) => {
                const doc = Array.isArray(res) ? (res[0] ?? null) : (res ?? null);
                setArticles(normalizeOrders(
                    [...(doc?.articles ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                ));
                setLoading(false);
            }
        ).catch(() => setLoading(false));
    }, [guide.routeId]);

    const handleExpand = () => {
        if (!expanded) {
            if ((guide.articleIds?.length ?? 0) > 0 || guide.hasGuide) {
                loadArticles();
            }
            setExpanded(true);
            return;
        }

        // When collapsing the guide, also collapse any expanded child article.
        setExpandedArticleId(null);
        setExpanded(false);
    };

    const saveReorder = useCallback(async (ordered) => {
        setReordering(true);
        try {
            await ApiCall(
                {
                    method: 'put',
                    url: `/guide/reorder?routeId=${encodeURIComponent(guide.routeId)}`,
                    data: { items: ordered.map(a => ({ articleId: a.id, order: a.order })) },
                },
                () => {}
            );
        } finally { setReordering(false); }
    }, [guide.routeId]);

    const handleDrop = (dropIndex) => {
        const dragIndex = dragIndexRef.current;
        if (dragIndex === null || dragIndex === dropIndex) {
            dragIndexRef.current = null; setDragOver(null); return;
        }
        const reordered = [...articles];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        const normalized = normalizeOrders(reordered);
        setArticles(normalized);
        dragIndexRef.current = null; setDragOver(null);
        saveReorder(normalized);
    };

    const resetInternalDrag = useCallback(() => {
        isDraggingInternallyRef.current = false;
        dragIndexRef.current = null;
        setDragOver(null);
        onArticleDragEnd?.();
    }, [onArticleDragEnd]);

    const handleTouchStartReorder = useCallback((e, article, index) => {
        if (!canEdit) return;
        e.stopPropagation();

        const touch = e.touches?.[0];
        isDraggingInternallyRef.current = true;
        dragIndexRef.current = index;
        setDragOver(index);
        onArticleDragStart?.(article, guide.routeId);

        touchDragRef.current = {
            active: true,
            sourceIndex: index,
            targetIndex: index,
            startY: touch?.clientY ?? 0,
            moved: false,
        };
    }, [canEdit, guide.routeId, onArticleDragStart]);

    const handleTouchMoveReorder = useCallback((e) => {
        const state = touchDragRef.current;
        if (!state.active) return;

        const touch = e.touches?.[0];
        if (!touch) return;

        if (Math.abs(touch.clientY - state.startY) > 6) {
            state.moved = true;
        }

        const elem = document.elementFromPoint(touch.clientX, touch.clientY);
        const row = elem?.closest?.('[data-article-index]');
        if (!row) return;

        const idx = Number(row.getAttribute('data-article-index'));
        if (Number.isNaN(idx)) return;

        state.targetIndex = idx;
        setDragOver(idx);

        // Prevent page scrolling while actively dragging an article.
        if (state.moved) e.preventDefault();
    }, []);

    const handleTouchEndReorder = useCallback((e) => {
        const state = touchDragRef.current;
        if (!state.active) return;

        e.stopPropagation();

        const { sourceIndex, targetIndex, moved } = state;
        touchDragRef.current = { active: false, sourceIndex: null, targetIndex: null, startY: 0, moved: false };

        if (moved && sourceIndex !== null && targetIndex !== null && sourceIndex !== targetIndex) {
            handleDrop(targetIndex);
        }

        resetInternalDrag();
    }, [handleDrop, resetInternalDrag]);

    const handleDeleteArticle = (article) => {
        setSelectedArticle(article);
        setDisplayDeleteArticleModal(true);
    };

    const handleModalDeleteArticle = async () => {
        if (!selectedArticle) return;
        await ApiCall(
            { method: 'delete', url: `/guide/article?routeId=${encodeURIComponent(guide.routeId)}&articleId=${encodeURIComponent(selectedArticle.id)}` },
            async (res) => {
                const normalized = normalizeOrders(
                    [...(res.articles ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                );
                if (normalized.length > 0) {
                    await ApiCall(
                        { method: 'put', url: `/guide/reorder?routeId=${encodeURIComponent(guide.routeId)}`, data: { items: normalized.map(a => ({ articleId: a.id, order: a.order })) } },
                        () => setArticles(normalized)
                    );
                } else { setArticles([]); }
                setDisplayDeleteArticleModal(false);
                setSelectedArticle(null);
            }
        );
    };

    const handleExtDropAtIndex = (e, insertAtIndex) => {
        e.preventDefault(); e.stopPropagation();
        setIsExtArtDragOver(false); setExternalDropIndex(null);
        const ds = dragStateRef.current;
        if (ds.draggingArticle && ds.draggingArticle.fromRouteId !== guide.routeId)
            onArticleDropFromOutside(ds.draggingArticle.article, ds.draggingArticle.fromRouteId, insertAtIndex + 1);
    };

    const handleExtDropOnCard = (e) => {
        e.preventDefault();
        setIsExtArtDragOver(false); setExternalDropIndex(null);
        const ds = dragStateRef.current;
        const isExtDrag = ds.draggingArticle && ds.draggingArticle.fromRouteId !== guide.routeId;
        if (isExtDrag) {
            if (!expanded) { loadArticles(); setExpanded(true); }
            onArticleDropFromOutside(ds.draggingArticle.article, ds.draggingArticle.fromRouteId, articles.length + 1);
        }
    };

    const effectiveArticleCount = articleCountOverride ?? (guide.articleIds?.length ?? 0);
    const hasArticles = effectiveArticleCount > 0 || guide.hasGuide;
    const hasGuideChildren = (childGuides?.length ?? 0) > 0;
    const hasExpandableContent = hasArticles || hasGuideChildren;
    const isTouchDraggingThisGuide = touchDragging?.type === 'guide' && String(touchDragging.id) === String(guide.id);
    const canDragGuide = canEdit && canDragContainers;
    const childOrderOverrideIds = handlers.childOrderOverrideByParent?.[String(guide.id)] ?? [];
    const orderedChildGuides = useMemo(
        () => reorderNodesByIds(childGuides ?? [], childOrderOverrideIds),
        [childGuides, childOrderOverrideIds]
    );

    const visibleChildGuides = useMemo(
        () => orderedChildGuides.filter((child) => {
            if (!hasVisibleNodeBySection(child, handlers.sectionExpandedMap)) return false;
            if (child?.type === 'guide') return isGuideVisibleBySection(child.guide, handlers.sectionExpandedMap);
            return true;
        }),
        [orderedChildGuides, handlers.sectionExpandedMap]
    );

    useEffect(() => {
        setIsExtArtDragOver(false);
        setIsGuideDropOver(false);
        setExternalDropIndex(null);
    }, [handlers.dragVisualResetTick]);

    useEffect(() => {
        if (expanded) {
            loadArticles();
        }
    }, [articleRefreshTick, expanded, loadArticles]);

    return (
        <>
        <CustomModal
            isOpen={displayDeleteArticleModal}
            toggle={() => { setDisplayDeleteArticleModal(false); setSelectedArticle(null); }}
            body={
                <div>
                    {t('validationMessage.modalDelete')}
                    {selectedArticle?.title && (
                        <p className="mt-2 text-muted"><strong>{selectedArticle.title}</strong></p>
                    )}
                </div>
            }
            footer={
                <>
                    <Button className="box-green border-0" onClick={handleModalDeleteArticle}>{t('button.delete')}</Button>
                    <Button className="box-red border-0" onClick={() => { setDisplayDeleteArticleModal(false); setSelectedArticle(null); }}>{t('button.cancel')}</Button>
                </>
            }
        />
        <div
            className={`gc-card ${depth > 0 ? 'gc-card--nested' : ''} ${expanded ? 'gc-card--open' : ''} ${isExtArtDragOver || isGuideDropOver || isTouchDraggingThisGuide ? 'gc-card--drop-target' : ''} ${!hasArticles ? 'gc-card--no-children' : ''}`}
            draggable={canDragGuide}
            onDragStart={(e) => {
                if (!canDragGuide) return;
                e.stopPropagation();
                onGuideDragStart?.(guide);
            }}
            onDragEnd={(e)   => { e.stopPropagation(); setIsGuideDropOver(false); onGuideDragEnd?.(); }}
            onDragOver={(e) => {
                if (isDraggingInternallyRef.current) return;
                const ds = dragStateRef.current;

                if (ds.draggingGuide && String(ds.draggingGuide.guide?.id) !== String(guide.id)) {
                    e.preventDefault();
                    setIsGuideDropOver(true);
                    setIsExtArtDragOver(false);
                    return;
                }

                if (ds.draggingArticle && ds.draggingArticle.fromRouteId !== guide.routeId) {
                    e.preventDefault(); setIsExtArtDragOver(true); return;
                }
            }}
            onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsExtArtDragOver(false);
                    setIsGuideDropOver(false);
                    setExternalDropIndex(null);
                }
            }}
            onDrop={(e) => {
                if (isDraggingInternallyRef.current) return;
                const ds = dragStateRef.current;

                if (ds.draggingGuide && String(ds.draggingGuide.guide?.id) !== String(guide.id)) {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsGuideDropOver(false);
                    setIsExtArtDragOver(false);
                    onGuideDropIntoGuide?.(ds.draggingGuide.guide, guide);
                    return;
                }

                if (ds.draggingArticle && ds.draggingArticle.fromRouteId !== guide.routeId) {
                    handleExtDropOnCard(e); return;
                }
            }}
        >
            <div className="gc-header">
                <button className="gc-toggle" onClick={hasExpandableContent ? handleExpand : undefined} style={{ cursor: hasExpandableContent ? 'pointer' : 'default' }}>
                    {canDragGuide && (
                        <span
                            className="gc-drag"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            title={t('guide.dragToMove') || 'Arrastrar para mover'}
                            onTouchStart={(e) => {
                                if (!canDragGuide) return;
                                e.stopPropagation();
                                onGuideTouchStart?.(guide, guide.containerId ?? null, e);
                            }}
                            onTouchMove={(e) => { e.stopPropagation(); onGuideTouchMove?.(e); }}
                            onTouchEnd={(e) => { e.stopPropagation(); onGuideTouchEnd?.(e); }}
                            onTouchCancel={(e) => { e.stopPropagation(); onGuideTouchEnd?.(e); }}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ cursor: 'grab', padding: '0 2px', color: '#9ca3af', fontSize: 18, display: 'flex', alignItems: 'center', flexShrink: 0, touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                        >
                            <i className="bx bx-grid-vertical gc-icon-color" />
                        </span>
                    )}
                    {hasExpandableContent && <i className={`bx ${expanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />}
                    {depth > 0 && (
                        <i className={`bx bx-subdirectory-right ${hasExpandableContent ? '' : 'ms-4 ps-2'}`} style={{ fontSize: 24, color: '#91bbff' }} />
                    )}
                    <div className="gc-meta">
                        <span className="gc-route">
                            {!guide.content || t(guide.content) === guide.content ? guide.routeId : t(guide.content)}
                            {hasArticles && <span className="ms-1">({effectiveArticleCount})</span>}
                        </span>
                        {guide.content && t(guide.content) !== guide.content && (
                            <span className="gc-desc">{guide.routeId}</span>
                        )}
                        {guide.description && <span className="gc-desc">{guide.description}</span>}
                    </div>
                </button>
                <div className="gc-actions">
                    {reordering && <Spinner size="sm" color="success" style={{ marginRight: 6 }} />}
                    <button className="gc-btn gc-btn--edit gc-btn--pill" onClick={() => onNewArticle(guide.routeId, articles.length)} title={t('guide.newArticle')}>
                        <i className="bx bxs-plus-circle" />
                    </button>
                    {canEdit && canDragContainers && (
                        <>
                            {/*<button className="gc-btn gc-btn--move" onClick={() => onMoveGuide(guide)} title={t('guide.moveGuide') || 'Mover guía'}>
                                <i className="bx bx-transfer-alt" />
                            </button>*/}
                            <button className="gc-btn gc-btn--edit gc-btn--pill" onClick={() => onEditGuide(guide)} title={t('button.edit')}>
                                <i className="bx bxs-pencil" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            {expanded && hasArticles && (
                <div className="gc-articles ps-4">
                    {loading ? (
                        <div className="gc-articles-loading"><Spinner size="sm" color="success" /></div>
                    ) : (
                        <>
                            {getIsExternalArticleDrag() && (
                                <div
                                    className={`gc-drop-zone ${externalDropIndex === 0 ? 'gc-drop-zone--over' : ''}`}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setExternalDropIndex(0); }}
                                    onDragLeave={() => setExternalDropIndex(null)}
                                    onDrop={(e) => handleExtDropAtIndex(e, 0)}
                                />
                            )}
                            {articles.map((article, index) => (
                                <div key={article.id}>
                                    <div
                                        data-article-index={index}
                                        className={`gc-article-row ${dragOver === index ? 'gc-article-row--over' : ''}`}
                                        draggable={canEdit}
                                        onDragStart={(e) => {
                                            e.stopPropagation();
                                            isDraggingInternallyRef.current = true;
                                            dragIndexRef.current = index;
                                            onArticleDragStart?.(article, guide.routeId);
                                        }}
                                        onDragEnd={(e) => {
                                            e.stopPropagation();
                                            isDraggingInternallyRef.current = false;
                                            dragIndexRef.current = null;
                                            setDragOver(null);
                                            onArticleDragEnd?.();
                                        }}
                                        onDragOver={(e) => {
                                            e.stopPropagation();
                                            if (!getIsExternalArticleDrag()) { e.preventDefault(); setDragOver(index); }
                                        }}
                                        onDrop={(e) => { e.stopPropagation(); if (!getIsExternalArticleDrag()) handleDrop(index); }}
                                    >
                                        <div className="gc-article-left" style={{ cursor: 'pointer' }} onClick={() => setExpandedArticleId(p => p === article.id ? null : article.id)}>
                                            {canEdit && (
                                                <span
                                                    className="gc-drag"
                                                    onClick={e => e.stopPropagation()}
                                                    onTouchStart={(e) => handleTouchStartReorder(e, article, index)}
                                                    onTouchMove={handleTouchMoveReorder}
                                                    onTouchEnd={handleTouchEndReorder}
                                                    onTouchCancel={handleTouchEndReorder}
                                                >
                                                    <i className="bx bx-grid-vertical gc-icon-color" />
                                                </span>
                                            )}
                                            {/*<span className="gc-order">{article.order}</span>*/}
                                            <i className={`bx ${expandedArticleId === article.id ? 'bx-chevron-up' : 'bx-chevron-down'} gc-chevron gc-icon-color`}/>
                                            <i className="bx bxs-file" style={{ fontSize: 24, color: '#006636' }}/>
                                            {/*<span className={`gc-vis ${article.isVisible ? 'gc-vis--on' : 'gc-vis--off'}`} />*/}
                                            <Row className="w-100">
                                                <Col xs={12} md={6}>
                                                    {article.title || '(ingen titel)'}
                                                </Col>
                                                <Col xs={12} md={3} className="text-secondary small">
                                                    {article.updatedAt ? t("guide.updatedAt") + ": " + ChangeDateFormat('date', article.updatedAt) : t("modelLabel.createdAt") + ": " + ChangeDateFormat('date', article.createdAt)}
                                                </Col>
                                                <Col xs={12} md={3}>
                                                    <span className={`gc-status-pill ${article.isVisible ? 'gc-status-pill--publicated' : 'gc-status-pill--draft'}`}>
                                                        {article.isVisible ? (t('guide.publicated') || 'Publicated') : (t('reimbursement.draft') || 'Draft')}
                                                    </span>
                                                </Col>
                                            </Row>
                                            {/*<span className="gc-article-title">{article.title || '(ingen titel)'}</span>*/}
                                        </div>
                                        {canEdit && (
                                            <div className="gc-article-actions">
                                                <button className="gc-icon-btn gc-icon-btn--edit gc-btn--pill" onClick={() => onEditArticle(guide.routeId, article, articles.length)} title={t('button.edit')}><i className="bx bxs-pencil" /></button>
                                            </div>
                                        )}
                                    </div>
                                    {expandedArticleId === article.id && (
                                        <div className="ms-5" style={{ padding: '12px 18px 14px', background: 'transparent', fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                                            {!isContentEmpty(article.content) ? (
                                                <div className="hp-content" dangerouslySetInnerHTML={{
                                                    __html: (article.content ?? '')
                                                        .replace(/<img /g, '<img style="max-width:100%;height:auto;display:block;margin:0.5rem 0;" ')
                                                        .replace(/<iframe /g, '<iframe style="max-width:100%;width:100%;aspect-ratio:16/9;height:auto;display:block;margin:0.5rem 0;" ')
                                                        .replace(/<video /g, '<video style="max-width:100%;height:auto;display:block;margin:0.5rem 0;" controls ')
                                                }} />
                                            ) : (
                                                <p style={{ color: '#9ca3af', margin: 0, fontStyle: 'italic' }}>{t('guide.noContent') || 'Inget innehåll ännu.'}</p>
                                            )}
                                        </div>
                                    )}
                                    {getIsExternalArticleDrag() && (
                                        <div
                                            className={`gc-drop-zone ${externalDropIndex === index + 1 ? 'gc-drop-zone--over' : ''}`}
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setExternalDropIndex(index + 1); }}
                                            onDragLeave={() => setExternalDropIndex(null)}
                                            onDrop={(e) => handleExtDropAtIndex(e, index + 1)}
                                        />
                                    )}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}

            {expanded && hasGuideChildren && (
                <div style={{ marginTop: 6 }}>
                    <GuideSiblingDropZone
                        insertIndex={0}
                        siblingGuides={visibleChildGuides}
                        dragStateRef={dragStateRef}
                        onReorderLevel={handlers.onReorderLevel}
                        canDropContainers={canDragContainers}
                        parentGroupId={guide.id}
                        dragVisualResetTick={handlers.dragVisualResetTick}
                    />
                    {visibleChildGuides.map((childNode, index) => (
                        <div key={`child-guide-slot-${childNode.id}`}>
                            {renderNodes([childNode], depth + 1, canEdit, canDelete, handlers)}
                            <GuideSiblingDropZone
                                insertIndex={index + 1}
                                siblingGuides={visibleChildGuides}
                                dragStateRef={dragStateRef}
                                onReorderLevel={handlers.onReorderLevel}
                                canDropContainers={canDragContainers}
                                parentGroupId={guide.id}
                                dragVisualResetTick={handlers.dragVisualResetTick}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}

// ── ContainerCard (recursive) ─────────────────────────────────────────────────
function ContainerCard({ node, depth = 0, canEdit, canDelete, handlers }) {
    const { t } = useTranslation();
    const [expanded,   setExpanded]   = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const canDragContainers = !!handlers.canDragContainers;
    const hasChildren = (node.children ?? []).length > 0;
    const isTouchOver = handlers.touchDropTarget?.type === 'container' && String(handlers.touchDropTarget?.containerId) === String(node.id);
    const isTouchDraggingThisContainer = handlers.touchDragging?.type === 'container' && String(handlers.touchDragging?.id) === String(node.id);

    // Never read dragStateRef during render — only read it inside event handlers.
    // Reading it during render gives a stale snapshot from when the component last rendered.

    useEffect(() => {
        setIsDragOver(false);
    }, [handlers.dragVisualResetTick]);

    return (
        <div
        className={`gc-container-card ${expanded ? 'gc-container-card--open' : ''} ${(isDragOver || isTouchOver || isTouchDraggingThisContainer) ? 'gc-container-card--drop-target' : ''} ${!hasChildren ? 'gc-container-card--no-children' : ''}`}
            data-container-drop-id={node.id}
            style={{ '--gc-depth': depth }}
            onDragOver={(e) => {
                if (!canDragContainers) return;
                const ds = handlers.dragStateRef.current;
                const isGD = ds.draggingGuide && ds.draggingGuide.containerId !== node.id;
                const isCD = ds.draggingContainer && ds.draggingContainer.id !== node.id;
                if (!isGD && !isCD) return;
                e.preventDefault(); e.stopPropagation();
                setIsDragOver(true);
            }}
            onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
            }}
            onDrop={(e) => {
                if (!canDragContainers) return;
                const ds = handlers.dragStateRef.current;
                const isGD = ds.draggingGuide && ds.draggingGuide.containerId !== node.id;
                const isCD = ds.draggingContainer && ds.draggingContainer.id !== node.id;
                if (!isGD && !isCD) return;
                e.preventDefault(); e.stopPropagation();
                setIsDragOver(false);
                if (isCD) handlers.onContainerDropInto?.(node.id);
                else if (isGD) handlers.onGuideDrop(node.id);
            }}
        >
            <div className="gc-container-header">
                <button className="gc-toggle" onClick={hasChildren ? () => setExpanded(p => !p) : undefined} style={{ cursor: hasChildren ? 'pointer' : 'default' }}>
                    {canEdit && canDragContainers && (
                        <span
                            className="gc-drag"
                            draggable={canEdit && canDragContainers}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDragStart={(e) => {
                                if (!canDragContainers) return;
                                e.stopPropagation();
                                handlers.onContainerDragStart?.(node);
                            }}
                            onDragEnd={(e)   => { e.stopPropagation(); handlers.onContainerDragEnd?.(); }}
                            onTouchStart={(e) => {
                                if (!canDragContainers) return;
                                e.stopPropagation();
                                handlers.onContainerTouchStart?.(node, e);
                            }}
                            onTouchMove={(e) => { e.stopPropagation(); handlers.onContainerTouchMove?.(e); }}
                            onTouchEnd={(e) => { e.stopPropagation(); handlers.onContainerTouchEnd?.(e); }}
                            onTouchCancel={(e) => { e.stopPropagation(); handlers.onContainerTouchEnd?.(e); }}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ cursor: 'grab', padding: '0 2px', color: '#9ca3af', fontSize: 18, display: 'flex', alignItems: 'center', flexShrink: 0, touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                            title={t('guide.dragToMove') || 'Arrastrar para mover'}
                        >
                            <i className="bx bx-grid-vertical gc-icon-color" />
                        </span>
                    )}
                    {hasChildren && <i className={`bx ${expanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />}
                    <i className="bx bx-folder gc-folder-icon" />
                    <span className="gc-container-name">{node.name}</span>
                    <span className="gc-badge gc-badge--container">{node.children?.length ?? 0}</span>
                </button>

                <div className="gc-actions">
                    {canEdit && (
                        <>
                            <button className="gc-btn gc-btn--edit" onClick={() => handlers.onNewContainer(node.id)} title={t('guide.newSubContainer')}>
                                <i className="bx bx-folder-plus" />
                            </button>
                            <button className="gc-btn gc-btn--edit" onClick={() => handlers.onEditContainer(node)} title={t('button.edit')}>
                                <i className="bx bxs-pencil" />
                            </button>
                        </>
                    )}
                    {canDelete && (
                        <button className="gc-btn gc-btn--delete" onClick={() => handlers.onDeleteContainer(node)} title={t('button.delete')}>
                            <i className="bx bx-trash-alt" />
                        </button>
                    )}
                </div>
            </div>

            {expanded && hasChildren && (
                <div className="gc-container-body">
                    {renderNodes(node.children, depth + 1, canEdit, canDelete, handlers)}
                </div>
            )}
        </div>
    );
}

// ── renderNodes ───────────────────────────────────────────────────────────────
function renderNodes(nodes, depth, canEdit, canDelete, handlers, nodeWrapperClassName = '') {
    const sectionExpandedMap = handlers.sectionExpandedMap;

    return nodes.map(node => {
        if (!hasVisibleNodeBySection(node, sectionExpandedMap)) {
            return null;
        }

        if (node.type === 'container') {
            return (
                <ContainerCard key={node.id} node={node} depth={depth} canEdit={canEdit} canDelete={canDelete} handlers={handlers} />
            );
        }
        const guide = node.guide;

        if (!isGuideVisibleBySection(guide, sectionExpandedMap)) {
            return null;
        }

        return (
            <div key={`guide-node-${node.id}`} className={nodeWrapperClassName} style={{ marginLeft: depth > 0 ? depth * 16 : 0 }}>
                {depth > 0 && <hr className="gc-nested-hr mx-4" />}
                <GuideCard
                    guide={guide}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onDeleteGuide={handlers.onDeleteGuide}
                    onEditGuide={handlers.onEditGuide}
                    onNewArticle={handlers.onNewArticle}
                    onEditArticle={handlers.onEditArticle}
                    onMoveGuide={handlers.onMoveGuide}
                    onGuideDropIntoGuide={handlers.onGuideDropIntoGuide}
                    childGuides={node.childGuides ?? []}
                    depth={depth}
                    handlers={handlers}
                    dragStateRef={handlers.dragStateRef}
                    onArticleDragStart={handlers.onArticleDragStart}
                    onArticleDragEnd={handlers.onArticleDragEnd}
                    onArticleDropFromOutside={(article, fromRouteId, toOrder) =>
                        handlers.onArticleDropFromOutside(article, fromRouteId, guide.routeId, toOrder)
                    }
                    onGuideDragStart={(g) => handlers.onGuideDragStart(g, guide.containerId ?? null)}
                    onGuideDragEnd={handlers.onGuideDragEnd}
                    onGuideDrop={handlers.onGuideDrop}
                    onGuideTouchStart={handlers.onGuideTouchStart}
                    onGuideTouchMove={handlers.onGuideTouchMove}
                    onGuideTouchEnd={handlers.onGuideTouchEnd}
                    canDragContainers={handlers.canDragContainers}
                    touchDragging={handlers.touchDragging}
                    articleCountOverride={handlers.articleCountByRoute?.[guide.routeId]}
                    articleRefreshTick={handlers.articleRefreshByRoute?.[guide.routeId] ?? 0}
                />
            </div>
        );
    });
}

// ── renderRootNodes ───────────────────────────────────────────────────────────
function renderRootNodes(
    tree,
    canEdit,
    canDelete,
    handlers,
    onReorderLevel,
    treeRef,
    dragStateRef,
    sectionExpandedMap,
    onToggleSection,
    subsectionExpandedMap,
    onToggleSubsection,
) {
    const sectionOrder = ['user', 'organization', 'admin', 'superAdmin', 'unsectioned'];
    const result = [];

    const pushNodesWithDropZones = (nodesToRender, sectionKeyPrefix, nodeWrapperClassName = '') => {
        nodesToRender.forEach((node) => {
            const insertIndex = tree.findIndex((treeNode) => String(treeNode?.id) === String(node?.id));
            if (insertIndex >= 0) {
                result.push(
                    <RootDropZone
                        key={`root-drop-before-${sectionKeyPrefix}-${String(node.id)}`}
                        insertIndex={insertIndex}
                        treeRef={treeRef}
                        dragStateRef={dragStateRef}
                        onReorderLevel={onReorderLevel}
                        onGuideDropToRoot={handlers.onGuideDropToRoot}
                        touchDropTarget={handlers.touchDropTarget}
                        canDropContainers={handlers.canDragContainers}
                        dragVisualResetTick={handlers.dragVisualResetTick}
                    />
                );
            }

            result.push(...renderNodes([node], 0, canEdit, canDelete, handlers, nodeWrapperClassName).filter(Boolean));
        });

        if (nodesToRender.length > 0) {
            const lastNode = nodesToRender[nodesToRender.length - 1];
            const lastNodeIndex = tree.findIndex((treeNode) => String(treeNode?.id) === String(lastNode?.id));
            if (lastNodeIndex >= 0) {
                result.push(
                    <RootDropZone
                        key={`root-drop-after-${sectionKeyPrefix}-${String(lastNode.id)}`}
                        insertIndex={lastNodeIndex + 1}
                        treeRef={treeRef}
                        dragStateRef={dragStateRef}
                        onReorderLevel={onReorderLevel}
                        onGuideDropToRoot={handlers.onGuideDropToRoot}
                        touchDropTarget={handlers.touchDropTarget}
                        canDropContainers={handlers.canDragContainers}
                        dragVisualResetTick={handlers.dragVisualResetTick}
                    />
                );
            }
        }
    };

    sectionOrder.forEach((sectionKey) => {
        const sectionNodes = filterNodesBySection(tree, sectionKey);
        if (sectionNodes.length === 0) return;

        const sectionTitle = GUIDE_SECTION_TITLES[sectionKey] ?? sectionKey;
        const expanded = !!sectionExpandedMap?.[sectionKey];

        result.push(
            <div key={`section-${sectionKey}`} className="guide-header d-flex align-items-center mb-2 mt-4 cursor-pointer-custom" onClick={() => onToggleSection?.(sectionKey)}>
                <i className={`bx ${expanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />
                <h6 className="ms-3 me-4">
                    {sectionTitle}
                </h6>
                <hr className="flex-grow-1 guide-header-hr" />
            </div>
        );

        if (!expanded) return;

        const visibleSectionNodes = sectionNodes.filter((node) => {
            if (!hasVisibleNodeBySection(node, sectionExpandedMap)) return false;
            if (node.type === 'guide') return isGuideVisibleBySection(node.guide, sectionExpandedMap);
            return true;
        });

        if (sectionKey === 'organization') {
            const collectNodes = visibleSectionNodes.filter((node) => hasOrganizationCollectRoute(node));
            const nonCollectNodes = visibleSectionNodes.filter((node) => !hasOrganizationCollectRoute(node));

            pushNodesWithDropZones(nonCollectNodes, `${sectionKey}-default`);

            if (collectNodes.length > 0) {
                const collectKey = 'organizationCollect';
                const collectExpanded = !!subsectionExpandedMap?.[collectKey];

                result.push(
                    <div key={`section-${sectionKey}-collect-wrapper`}>
                        <div
                            key={`section-${sectionKey}-collect`}
                            className="guide-header d-flex align-items-center mb-2 mt-3 cursor-pointer-custom ms-4"
                            onClick={() => onToggleSubsection?.(collectKey)}
                        >
                            <i className={`bx ${collectExpanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />
                            <h6 className="ms-3 me-4">{t("modelLabel.collect")}</h6>
                            <hr className="flex-grow-1 guide-header-hr" />
                        </div>
                    </div>
                );

                if (collectExpanded) {
                    pushNodesWithDropZones(collectNodes, `${sectionKey}-collect`, 'ms-4');
                }
            }
            return;
        }

        if (sectionKey === 'admin') {
            const reportsNodes = visibleSectionNodes.filter((node) => hasAdminReportsRoute(node));
            const nonReportsNodes = visibleSectionNodes.filter((node) => !hasAdminReportsRoute(node));

            pushNodesWithDropZones(nonReportsNodes, `${sectionKey}-default`);

            if (reportsNodes.length > 0) {
                const reportsKey = 'adminReports';
                const reportsExpanded = !!subsectionExpandedMap?.[reportsKey];

                result.push(
                    <div key={`section-${sectionKey}-reports-wrapper`}>
                        <div
                            key={`section-${sectionKey}-reports`}
                            className="guide-header d-flex align-items-center mb-2 mt-3 cursor-pointer-custom ms-4"
                            onClick={() => onToggleSubsection?.(reportsKey)}
                        >
                            <i className={`bx ${reportsExpanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />
                            <h6 className="ms-3 me-4">{t("modelLabel.reports")}</h6>
                            <hr className="flex-grow-1 guide-header-hr" />
                        </div>
                    </div>
                );

                if (reportsExpanded) {
                    pushNodesWithDropZones(reportsNodes, `${sectionKey}-reports`, 'ms-4');
                }
            }
            return;
        }

        if (sectionKey === 'superAdmin') {
            const settingsNodes = visibleSectionNodes.filter((node) => hasSuperAdminSettingsRoute(node));
            const nonSettingsNodes = visibleSectionNodes.filter((node) => !hasSuperAdminSettingsRoute(node));

            pushNodesWithDropZones(nonSettingsNodes, `${sectionKey}-default`);

            if (settingsNodes.length > 0) {
                const settingsKey = 'superAdminSettings';
                const settingsExpanded = !!subsectionExpandedMap?.[settingsKey];

                result.push(
                    <div key={`section-${sectionKey}-settings-wrapper`}>
                        <div
                            key={`section-${sectionKey}-settings`}
                            className="guide-header d-flex align-items-center mb-2 mt-3 cursor-pointer-custom ms-4"
                            onClick={() => onToggleSubsection?.(settingsKey)}
                        >
                            <i className={`bx ${settingsExpanded ? 'bx-chevron-up' : 'bx-chevron-right'} gc-chevron gc-icon-color`} />
                            <h6 className="ms-3 me-4">{t("modelLabel.settings", "Settings")}</h6>
                            <hr className="flex-grow-1 guide-header-hr" />
                        </div>
                    </div>
                );

                if (settingsExpanded) {
                    pushNodesWithDropZones(settingsNodes, `${sectionKey}-settings`, 'ms-4');
                }
            }
            return;
        }

        pushNodesWithDropZones(visibleSectionNodes, sectionKey);
    });

    return result;
}

// ── AdminGuideList ────────────────────────────────────────────────────────────
const AdminGuideList = () => {
    const { t } = useTranslation();
    const { rolesData } = useSelector(state => state.auth);
    const { ApiCall } = useApiCallService();
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    const [containers,      setContainers]      = useState([]);
    //const [containersReady, setContainersReady] = useState(false);

    const [displayDeleteModal,          setDisplayDeleteModal]          = useState(false);
    const [deleteApiResponse,           setDeleteApiResponse]           = useState(null);
    const [selectedGuide,               setSelectedGuide]               = useState(null);
    const [displayDeleteContainerModal, setDisplayDeleteContainerModal] = useState(false);
    const [selectedContainer,           setSelectedContainer]           = useState(null);

    const [articleFormOpen,   setArticleFormOpen]   = useState(false);
    const [articleFormRoute,  setArticleFormRoute]  = useState(null);
    const [articleFormData,   setArticleFormData]   = useState(null);
    const [articleFormSaving, setArticleFormSaving] = useState(false);
    const [articleFormTotal,  setArticleFormTotal]  = useState(0);
    const [displayDeleteArticleFormModal, setDisplayDeleteArticleFormModal] = useState(false);

    const [guideFormOpen,   setGuideFormOpen]   = useState(false);
    const [guideFormData,   setGuideFormData]   = useState(null);
    const [guideFormSaving, setGuideFormSaving] = useState(false);

    const [containerFormOpen,   setContainerFormOpen]   = useState(false);
    const [containerFormData,   setContainerFormData]   = useState(null);
    const [containerFormParent, setContainerFormParent] = useState(null);
    const [containerFormSaving, setContainerFormSaving] = useState(false);

    const [pendingDrop,     setPendingDrop]     = useState(null);
    const [moveGuideTarget, setMoveGuideTarget] = useState(null);
    const [touchDragging, setTouchDragging] = useState(null);
    const [touchDropTarget, setTouchDropTarget] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [expandedSubsections, setExpandedSubsections] = useState({
        organizationCollect: false,
        adminReports: false,
        superAdminSettings: false,
    });
    const [rootOrderOverrideIds, setRootOrderOverrideIds] = useState([]);
    const [childOrderOverrideByParent, setChildOrderOverrideByParent] = useState({});
    const [forcedRootGuideIds, setForcedRootGuideIds] = useState([]);
    const [forcedParentByGuideId, setForcedParentByGuideId] = useState({});
    const [articleCountByRoute, setArticleCountByRoute] = useState({});
    const [articleRefreshByRoute, setArticleRefreshByRoute] = useState({});
    const [guideOverridesByRoute, setGuideOverridesByRoute] = useState({});
    const [createdGuidesByRoute, setCreatedGuidesByRoute] = useState({});
    const [deletedGuideRouteIds, setDeletedGuideRouteIds] = useState([]);
    const [dragVisualResetTick, setDragVisualResetTick] = useState(0);

    const touchGuideDragRef = useRef({ active: false, moved: false, startY: 0, sourceContainerId: null });
    const touchContainerDragRef = useRef({ active: false, moved: false, startY: 0 });

    // ── Single shared ref for ALL drag state ──────────────────────────────────
    const dragStateRef = useRef({
        draggingGuide:     null,
        draggingContainer: null,
        draggingArticle:   null,
    });
    const isDraggingRef = useRef(false);
    const autoScrollFrameRef = useRef(null);
    const lastPointerYRef = useRef(null);

    const treeRef = useRef([]);

    const [reloadKey, setReloadKey] = useState(0);
    const triggerReload = useCallback(() => setReloadKey(k => k + 1), []);
    const handleToggleSection = useCallback((sectionKey) => {
        setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);
    const handleToggleSubsection = useCallback((subsectionKey) => {
        setExpandedSubsections((prev) => ({ ...prev, [subsectionKey]: !prev[subsectionKey] }));
    }, []);

    const mergeGuidesForRender = useCallback((items = []) => {
        const passthrough = [];
        const byRoute = new Map();
        const deletedRouteSet = new Set((deletedGuideRouteIds ?? []).map((routeId) => String(routeId)));

        (items ?? []).forEach((item) => {
            const routeId = item?.routeId != null ? String(item.routeId) : '';
            if (!routeId) {
                passthrough.push({ ...item });
                return;
            }
            if (deletedRouteSet.has(routeId)) return;
            byRoute.set(routeId, { ...item });
        });

        Object.entries(guideOverridesByRoute ?? {}).forEach(([routeId, patch]) => {
            if (deletedRouteSet.has(String(routeId))) return;
            const current = byRoute.get(routeId) ?? { routeId };
            byRoute.set(routeId, { ...current, ...patch });
        });

        Object.entries(createdGuidesByRoute ?? {}).forEach(([routeId, guide]) => {
            if (deletedRouteSet.has(String(routeId))) return;
            const current = byRoute.get(routeId) ?? {};
            byRoute.set(routeId, { ...current, ...guide });
        });

        return [...passthrough, ...Array.from(byRoute.values())];
    }, [createdGuidesByRoute, deletedGuideRouteIds, guideOverridesByRoute]);

    const stopAutoScroll = useCallback(() => {
        isDraggingRef.current = false;
        lastPointerYRef.current = null;
        if (autoScrollFrameRef.current) {
            cancelAnimationFrame(autoScrollFrameRef.current);
            autoScrollFrameRef.current = null;
        }
    }, []);

    const startAutoScroll = useCallback(() => {
        isDraggingRef.current = true;
        if (autoScrollFrameRef.current) return;

        const step = () => {
            if (!isDraggingRef.current) {
                autoScrollFrameRef.current = null;
                return;
            }

            const pointerY = lastPointerYRef.current;
            if (typeof pointerY === 'number') {
                const edgeThreshold = 120;
                const maxSpeed = 18;
                const viewportHeight = window.innerHeight;
                let delta = 0;

                if (pointerY < edgeThreshold) {
                    delta = -Math.ceil(((edgeThreshold - pointerY) / edgeThreshold) * maxSpeed);
                } else if (pointerY > viewportHeight - edgeThreshold) {
                    delta = Math.ceil(((pointerY - (viewportHeight - edgeThreshold)) / edgeThreshold) * maxSpeed);
                }

                if (delta !== 0) {
                    window.scrollBy(0, delta);
                }
            }

            autoScrollFrameRef.current = requestAnimationFrame(step);
        };

        autoScrollFrameRef.current = requestAnimationFrame(step);
    }, []);

    useEffect(() => {
        const handleDragOver = (e) => {
            if (!isDraggingRef.current) return;
            lastPointerYRef.current = e.clientY;
        };
        const handleDragStop = () => stopAutoScroll();

        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('drop', handleDragStop);
        window.addEventListener('dragend', handleDragStop);

        return () => {
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('drop', handleDragStop);
            window.removeEventListener('dragend', handleDragStop);
            stopAutoScroll();
        };
    }, [stopAutoScroll]);

    /*const loadContainers = useCallback(() => {
        ApiCall(
            { method: 'get', url: '/guide/tree' },
            (res) => {
                setContainers(res ?? []);
                setContainersReady(true);
                setRootOrderOverrideIds([]);
                setChildOrderOverrideByParent({});
                setForcedRootGuideIds([]);
                setForcedParentByGuideId({});
            }
        );
    }, []);*/

    //useEffect(() => { loadContainers(); }, [loadContainers]);

    useEffect(() => {
        setPageTitle(t('guide.guides', 'Guides'));
        setPageTitleKey('guide.guides');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const canEdit = (
        CheckUserPermission("system.setting.guide.update") ||
        CheckUserPermission("system.setting.guide") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system")
    );
    const canDelete = (
        CheckUserPermission("system.setting.guide.delete") ||
        CheckUserPermission("system.setting.guide") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system")
    );
    const canDragContainers = CheckUserRole(rolesData, "SuperAdmin");

    // ── Reorder level ─────────────────────────────────────────────────────────
    const handleReorderLevel = useCallback(async (orderedNodes, parentContainerId) => {
        dragStateRef.current = { draggingGuide: null, draggingContainer: null, draggingArticle: null };
        stopAutoScroll();
        setDragVisualResetTick((tick) => tick + 1);
        const payload = buildReorderPayload(orderedNodes, parentContainerId);
        const isRootLevel = parentContainerId == null;
        const parentKey = String(parentContainerId);
        const previousRootOrderIds = rootOrderOverrideIds;
        const previousChildOrderByParent = childOrderOverrideByParent;

        if (isRootLevel) {
            setRootOrderOverrideIds(toNodeIdList(orderedNodes));
        } else {
            setChildOrderOverrideByParent((prev) => ({
                ...prev,
                [parentKey]: toNodeIdList(orderedNodes),
            }));
        }

        try {
            await ApiCall(
                { method: 'put', url: '/guide/reorder-guides', data: payload },
                () => {}
            );
        } catch (error) {
            if (isRootLevel) {
                setRootOrderOverrideIds(previousRootOrderIds);
            } else {
                setChildOrderOverrideByParent(previousChildOrderByParent);
            }
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    }, [ApiCall, childOrderOverrideByParent, rootOrderOverrideIds, stopAutoScroll, t]);

    const reorderToRootByTouch = useCallback((draggedNode, insertIndex) => {
        const tree = treeRef.current ?? [];
        const withoutDragged = tree.filter(n => String(n.id) !== String(draggedNode.id));
        const originalIndex = tree.findIndex(n => String(n.id) === String(draggedNode.id));
        const adjustedIndex = originalIndex >= 0 && insertIndex > originalIndex ? insertIndex - 1 : insertIndex;
        const boundedIndex = Math.max(0, Math.min(adjustedIndex, withoutDragged.length));

        // No-op drop: same resulting position, skip API/reload.
        if (originalIndex >= 0 && boundedIndex === originalIndex) return;

        const newTree = [...withoutDragged];
        newTree.splice(boundedIndex, 0, draggedNode);
        handleReorderLevel(newTree, null);
    }, [handleReorderLevel]);

    // ── Container drag ────────────────────────────────────────────────────────
    const handleContainerDragStart = useCallback((node) => {
        if (!canDragContainers) return;
        dragStateRef.current = { ...dragStateRef.current, draggingContainer: node };
        startAutoScroll();
    }, [canDragContainers, startAutoScroll]);

    const handleContainerDragEnd = useCallback(() => {
        setTimeout(() => {
            dragStateRef.current = { ...dragStateRef.current, draggingContainer: null };
            stopAutoScroll();
            setDragVisualResetTick((tick) => tick + 1);
        }, 50);
    }, [stopAutoScroll]);

    const handleContainerDropInto = useCallback(async (targetContainerId) => {
        if (!canDragContainers) return;
        const node = dragStateRef.current.draggingContainer;
        if (!node || node.id === targetContainerId) return;
        dragStateRef.current = { ...dragStateRef.current, draggingContainer: null };
        stopAutoScroll();
        await ApiCall(
            {
                method: 'put',
                url: `/guidecontainer/${encodeURIComponent(node.id)}`,
                data: { name: node.name, parentId: targetContainerId, order: 9999 },
            },
            //() => loadContainers()
        );
    }, [canDragContainers/*, loadContainers*/, stopAutoScroll]);

    const handleGuideTouchStart = useCallback((guide, containerId, e) => {
        if (!canEdit) return;
        const touch = e.touches?.[0];
        touchGuideDragRef.current = {
            active: true,
            moved: false,
            startY: touch?.clientY ?? 0,
            sourceContainerId: containerId ?? null,
        };
        setTouchDragging({ type: 'guide', id: guide.id });
        setTouchDropTarget(null);
        dragStateRef.current = { ...dragStateRef.current, draggingGuide: { guide, containerId: containerId ?? null } };
        startAutoScroll();
    }, [canEdit, startAutoScroll]);

    const handleGuideTouchMove = useCallback((e) => {
        const state = touchGuideDragRef.current;
        if (!state.active) return;
        const touch = e.touches?.[0];
        if (!touch) return;

        lastPointerYRef.current = touch.clientY;
        if (Math.abs(touch.clientY - state.startY) > 6) {
            state.moved = true;
            e.preventDefault();
            setTouchDropTarget(getTouchDropTarget(touch.clientX, touch.clientY));
        }
    }, []);

    const handleGuideDrop = useCallback(async (targetContainerId) => {
        const current = dragStateRef.current.draggingGuide;
        if (!current) return;
        dragStateRef.current = { ...dragStateRef.current, draggingGuide: null };
        stopAutoScroll();
        const { routeId } = current.guide;
        await ApiCall(
            {
                method: 'put',
                url: `/guidecontainer/assign-guide?routeId=${encodeURIComponent(routeId)}`,
                data: { containerId: targetContainerId, order: 9999 },
            },
            () => { /*loadContainers();*/ triggerReload(); }
        );
    }, [/*loadContainers,*/ triggerReload, stopAutoScroll]);

    const handleGuideDragEnd = useCallback(() => {
        setTimeout(() => {
            dragStateRef.current = { ...dragStateRef.current, draggingGuide: null };
            stopAutoScroll();
            setDragVisualResetTick((tick) => tick + 1);
        }, 50);
    }, [stopAutoScroll]);
    
    const handleGuideTouchEnd = useCallback((e) => {
        const state = touchGuideDragRef.current;
        if (!state.active) return;

        const touch = e.changedTouches?.[0];
        touchGuideDragRef.current = { active: false, moved: false, startY: 0, sourceContainerId: null };
        setTouchDropTarget(null);
        setTouchDragging(null);

        if (state.moved && touch) {
            const dropTarget = getTouchDropTarget(touch.clientX, touch.clientY);
            const targetContainerId = dropTarget?.type === 'container' ? dropTarget.containerId : null;

            if (dropTarget?.type === 'root' && typeof dropTarget.rootIndex === 'number') {
                const current = dragStateRef.current.draggingGuide;
                if (current?.guide) {
                    const draggedNode = {
                        type: 'guide',
                        id: current.guide.id,
                        guide: current.guide,
                        order: current.guide.order ?? 0,
                    };
                    reorderToRootByTouch(draggedNode, dropTarget.rootIndex);
                    return;
                }
            }

            if (!targetContainerId) {
                if (dropTarget?.type === 'root' && state.sourceContainerId != null) {
                    handleGuideDrop(null);
                    return;
                }
            }

            if (targetContainerId && String(targetContainerId) !== String(state.sourceContainerId ?? '')) {
                handleGuideDrop(targetContainerId);
                return;
            }
        }

        handleGuideDragEnd();
    }, [handleGuideDrop, handleGuideDragEnd, reorderToRootByTouch]);

    const handleContainerTouchStart = useCallback((node, e) => {
        if (!canEdit || !canDragContainers) return;
        const touch = e.touches?.[0];
        touchContainerDragRef.current = {
            active: true,
            moved: false,
            startY: touch?.clientY ?? 0,
        };
        setTouchDragging({ type: 'container', id: node.id });
        setTouchDropTarget(null);
        handleContainerDragStart(node);
    }, [canEdit, canDragContainers, handleContainerDragStart]);

    const handleContainerTouchMove = useCallback((e) => {
        const state = touchContainerDragRef.current;
        if (!state.active) return;
        const touch = e.touches?.[0];
        if (!touch) return;

        lastPointerYRef.current = touch.clientY;
        if (Math.abs(touch.clientY - state.startY) > 6) {
            state.moved = true;
            e.preventDefault();
            setTouchDropTarget(getTouchDropTarget(touch.clientX, touch.clientY));
        }
    }, []);

    const handleContainerTouchEnd = useCallback((e) => {
        const state = touchContainerDragRef.current;
        if (!state.active) return;

        const touch = e.changedTouches?.[0];
        touchContainerDragRef.current = { active: false, moved: false, startY: 0 };
        setTouchDropTarget(null);
        setTouchDragging(null);

        if (state.moved && touch) {
            const dragged = dragStateRef.current.draggingContainer;
            const dropTarget = getTouchDropTarget(touch.clientX, touch.clientY);
            const targetContainerId = dropTarget?.type === 'container' ? dropTarget.containerId : null;

            if (dragged && dropTarget?.type === 'root' && typeof dropTarget.rootIndex === 'number') {
                if (!canDragContainers) {
                    handleContainerDragEnd();
                    return;
                }
                const draggedNode = {
                    type: 'container',
                    id: dragged.id,
                    ...dragged,
                };
                reorderToRootByTouch(draggedNode, dropTarget.rootIndex);
                return;
            }

            if (!targetContainerId) {
                if (dragged && dropTarget?.type === 'root' && dragged.parentId != null) {
                    handleContainerDropInto(null);
                    return;
                }
            }

            if (dragged && targetContainerId && String(targetContainerId) !== String(dragged.id)) {
                handleContainerDropInto(targetContainerId);
                return;
            }
        }

        handleContainerDragEnd();
    }, [canDragContainers, handleContainerDropInto, handleContainerDragEnd, reorderToRootByTouch]);

    // ── Guide delete ──────────────────────────────────────────────────────────
    const handleDeleteGuide = useCallback((guide) => { setSelectedGuide(guide); setDisplayDeleteModal(true); }, []);
    const handleModalDelete = async () => {
        try {
            await ApiCall(
                { method: 'delete', url: `/guide?routeId=${encodeURIComponent(selectedGuide.routeId)}` },
                setDeleteApiResponse
            );
            setDisplayDeleteModal(false);
        } catch { FlashMessage(t('validationMessage.sendError'), 'flash-messageError'); }
    };

    const handleCloseGuideForm = useCallback(() => {
        setGuideFormOpen(false);
        setGuideFormData(null);
    }, []);
    
    useEffect(() => {
        if (deleteApiResponse !== null) {
            FlashMessage(t('guide.deleted', 'Guide deleted'), 'flash-messageSuccess');
            const deletedRouteId = selectedGuide?.routeId != null ? String(selectedGuide.routeId) : null;
            const deletedGuideId = selectedGuide?.id != null ? String(selectedGuide.id) : null;

            if (deletedRouteId) {
                setDeletedGuideRouteIds((prev) => (prev.includes(deletedRouteId) ? prev : [...prev, deletedRouteId]));
                setGuideOverridesByRoute((prev) => {
                    if (!(deletedRouteId in prev)) return prev;
                    const next = { ...prev };
                    delete next[deletedRouteId];
                    return next;
                });
                setCreatedGuidesByRoute((prev) => {
                    if (!(deletedRouteId in prev)) return prev;
                    const next = { ...prev };
                    delete next[deletedRouteId];
                    return next;
                });
                setArticleCountByRoute((prev) => {
                    if (!(deletedRouteId in prev)) return prev;
                    const next = { ...prev };
                    delete next[deletedRouteId];
                    return next;
                });
                setArticleRefreshByRoute((prev) => {
                    if (!(deletedRouteId in prev)) return prev;
                    const next = { ...prev };
                    delete next[deletedRouteId];
                    return next;
                });
            }

            if (deletedGuideId) {
                setRootOrderOverrideIds((prev) => prev.filter((id) => String(id) !== deletedGuideId));
                setForcedRootGuideIds((prev) => prev.filter((id) => String(id) !== deletedGuideId));
                setForcedParentByGuideId((prev) => {
                    if (!(deletedGuideId in prev)) return prev;
                    const next = { ...prev };
                    delete next[deletedGuideId];
                    return next;
                });
            }

            if (guideFormOpen) {
                handleCloseGuideForm();
            }

            setDeleteApiResponse(null);
            setSelectedGuide(null);
        }
    }, [deleteApiResponse, guideFormOpen, handleCloseGuideForm, selectedGuide, t]);

    // ── Container delete ──────────────────────────────────────────────────────
    const handleDeleteContainer = useCallback((node) => { setSelectedContainer(node); setDisplayDeleteContainerModal(true); }, []);
    const handleModalDeleteContainer = async () => {
        try {
            await ApiCall(
                { method: 'delete', url: `/guidecontainer/${encodeURIComponent(selectedContainer.id)}` },
                () => {
                    FlashMessage(t('guide.containerDeleted', 'Container deleted'), 'flash-messageSuccess');
                    setDisplayDeleteContainerModal(false);
                    //loadContainers();
                    triggerReload();
                }
            );
        } catch { FlashMessage(t('validationMessage.sendError'), 'flash-messageError'); }
    };

    // ── Article form ──────────────────────────────────────────────────────────
    const handleOpenNewArticle   = useCallback((routeId, total) => { setArticleFormRoute(routeId); setArticleFormData(null); setArticleFormTotal(total); setArticleFormOpen(true); }, []);
    const handleOpenEditArticle  = useCallback((routeId, article, total) => { setArticleFormRoute(routeId); setArticleFormData(article); setArticleFormTotal(total); setArticleFormOpen(true); }, []);
    const handleCloseArticleForm = useCallback(() => {
        setDisplayDeleteArticleFormModal(false);
        setArticleFormOpen(false);
        setArticleFormData(null);
        setArticleFormRoute(null);
    }, []);

    const handleAskDeleteArticleFromForm = useCallback(() => {
        if (!articleFormData?.id) return;
        setDisplayDeleteArticleFormModal(true);
    }, [articleFormData]);

    const handleModalDeleteArticleFromForm = useCallback(async () => {
        const routeId = articleFormRoute;
        const articleId = articleFormData?.id;
        if (!routeId || !articleId) return;

        setArticleFormSaving(true);
        try {
            let normalizedArticles = [];

            await ApiCall(
                {
                    method: 'delete',
                    url: `/guide/article?routeId=${encodeURIComponent(routeId)}&articleId=${encodeURIComponent(articleId)}`,
                },
                async (res) => {
                    normalizedArticles = normalizeOrders(
                        [...(res?.articles ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    );

                    if (normalizedArticles.length > 0) {
                        await ApiCall(
                            {
                                method: 'put',
                                url: `/guide/reorder?routeId=${encodeURIComponent(routeId)}`,
                                data: { items: normalizedArticles.map((a) => ({ articleId: a.id, order: a.order })) },
                            },
                            () => {}
                        );
                    }
                }
            );

            setArticleCountByRoute((prev) => ({
                ...prev,
                [routeId]: normalizedArticles.length,
            }));
            setArticleRefreshByRoute((prev) => ({
                ...prev,
                [routeId]: (prev?.[routeId] ?? 0) + 1,
            }));

            FlashMessage(t('guide.articleDeleted', 'Article deleted'), 'flash-messageSuccess');
            handleCloseArticleForm();
        } catch {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            setArticleFormSaving(false);
        }
    }, [ApiCall, articleFormData, articleFormRoute, handleCloseArticleForm, t]);

    const handleOpenNewGuide = useCallback(() => {
        setGuideFormData(null);
        setGuideFormOpen(true);
    }, []);

    const handleOpenEditGuide = useCallback((guide) => {
        setGuideFormData(guide);
        setGuideFormOpen(true);
    }, []);

    const handleSaveArticle = async ({ title, content, isVisible, order }) => {
        setArticleFormSaving(true);
        try {
            const isEdit = !!articleFormData;
            const routeId = articleFormRoute;
            await ApiCall(
                {
                    method: isEdit ? 'put' : 'post',
                    url: isEdit
                        ? `/guide/article?routeId=${encodeURIComponent(routeId)}&articleId=${encodeURIComponent(articleFormData.id)}`
                        : `/guide/article?routeId=${encodeURIComponent(routeId)}`,
                    data: { articleId: articleFormData?.id ?? null, title, content, isVisible, order },
                },
                () => {}
            );

            await ApiCall(
                { method: 'get', url: `/guide/by-route?routeId=${encodeURIComponent(routeId)}` },
                (res) => {
                    const list = Array.isArray(res) ? res : [];
                    setArticleCountByRoute((prev) => ({
                        ...prev,
                        [routeId]: list.length,
                    }));
                }
            );

            setArticleRefreshByRoute((prev) => ({
                ...prev,
                [routeId]: (prev?.[routeId] ?? 0) + 1,
            }));
            handleCloseArticleForm();
        } finally { setArticleFormSaving(false); }
    };

    const handleSaveGuide = async ({ routeId, description, content }) => {
        const trimmedRouteId = (routeId ?? '').trim();
        if (!trimmedRouteId) return;

        setGuideFormSaving(true);
        try {
            if (guideFormData?.id) {
                await ApiCall(
                    {
                        method: 'put',
                        url: `/guide?routeId=${encodeURIComponent(guideFormData.routeId)}`,
                        data: {
                            id: guideFormData.id,
                            description: description ?? '',
                            content: content ?? null,
                        },
                    },
                    () => {}
                );
            } else {
                await ApiCall(
                    {
                        method: 'post',
                        url: '/guide',
                        data: {
                            routeId: trimmedRouteId,
                            description: description ?? '',
                            content: content ?? null,
                            hasGuide: false,
                        },
                    },
                    () => {}
                );
            }

            let refreshedGuide = null;
            await ApiCall(
                { method: 'get', url: `/guide/by-route?routeId=${encodeURIComponent(trimmedRouteId)}` },
                (response) => {
                    if (response && !Array.isArray(response)) {
                        refreshedGuide = response;
                    }
                }
            );

            const normalizedGuide = refreshedGuide ?? {
                id: guideFormData?.id ?? trimmedRouteId,
                routeId: trimmedRouteId,
                description: description ?? '',
                content: content ?? null,
                hasGuide: false,
            };

            setGuideOverridesByRoute((prev) => ({
                ...prev,
                [String(normalizedGuide.routeId)]: normalizedGuide,
            }));
            setDeletedGuideRouteIds((prev) => prev.filter((route) => String(route) !== String(normalizedGuide.routeId)));

            const normalizedRouteId = String(normalizedGuide.routeId ?? trimmedRouteId);
            const normalizedSectionKey = getGuideSectionMeta(normalizedRouteId)?.key;
            if (normalizedSectionKey) {
                setExpandedSections((prev) => ({ ...prev, [normalizedSectionKey]: true }));

                if (normalizedSectionKey === 'organization' && isOrganizationCollectRoute(normalizedRouteId)) {
                    setExpandedSubsections((prev) => ({ ...prev, organizationCollect: true }));
                }

                if (normalizedSectionKey === 'admin' && isAdminReportsRoute(normalizedRouteId)) {
                    setExpandedSubsections((prev) => ({ ...prev, adminReports: true }));
                }

                if (normalizedSectionKey === 'superAdmin' && isSuperAdminSettingsRoute(normalizedRouteId)) {
                    setExpandedSubsections((prev) => ({ ...prev, superAdminSettings: true }));
                }
            }

            if (!guideFormData?.id) {
                setCreatedGuidesByRoute((prev) => ({
                    ...prev,
                    [String(normalizedGuide.routeId)]: normalizedGuide,
                }));
            }

            handleCloseGuideForm();
        } catch {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            setGuideFormSaving(false);
        }
    };

    // ── Container form ────────────────────────────────────────────────────────
    const handleOpenNewContainer   = useCallback((parentId = null) => { setContainerFormData(null); setContainerFormParent(parentId); setContainerFormOpen(true); }, []);
    const handleOpenEditContainer  = useCallback((node) => { setContainerFormData(node); setContainerFormParent(null); setContainerFormOpen(true); }, []);
    const handleCloseContainerForm = useCallback(() => { setContainerFormOpen(false); setContainerFormData(null); setContainerFormParent(null); }, []);

    const handleSaveContainer = async ({ name, parentId }) => {
        setContainerFormSaving(true);
        try {
            if (containerFormData) {
                await ApiCall(
                    { method: 'put', url: `/guidecontainer/${encodeURIComponent(containerFormData.id)}`, data: { name, parentId: containerFormData.parentId ?? null, order: containerFormData.order ?? 1 } },
                    () => { handleCloseContainerForm(); /*loadContainers();*/ }
                );
            } else {
                await ApiCall(
                    { method: 'post', url: '/guidecontainer', data: { name, parentId: parentId ?? null, order: 1 } },
                    () => { handleCloseContainerForm(); /*loadContainers();*/ }
                );
            }
        } finally { setContainerFormSaving(false); }
    };

    // ── Article drag ──────────────────────────────────────────────────────────
    const handleArticleDropFromOutside = useCallback((article, fromRouteId, toRouteId, toOrder) => {
        setPendingDrop({ article, fromRouteId, toRouteId, toOrder });
    }, []);

    const handleConfirmMove = useCallback(async () => {
        if (!pendingDrop) return;
        const { article, fromRouteId, toRouteId, toOrder } = pendingDrop;
        setPendingDrop(null);
        await ApiCall(
            { method: 'put', url: `/guide/move-article?fromRouteId=${encodeURIComponent(fromRouteId)}&toRouteId=${encodeURIComponent(toRouteId)}&articleId=${encodeURIComponent(article.id)}&toOrder=${toOrder}` },
            () => triggerReload()
        );
    }, [pendingDrop]);

    const handleConfirmCopy = useCallback(async () => {
        if (!pendingDrop) return;
        const { article, fromRouteId, toRouteId, toOrder } = pendingDrop;
        setPendingDrop(null);
        await ApiCall(
            { method: 'post', url: `/guide/copy-article?fromRouteId=${encodeURIComponent(fromRouteId)}&toRouteId=${encodeURIComponent(toRouteId)}&articleId=${encodeURIComponent(article.id)}&toOrder=${toOrder}` },
            () => triggerReload()
        );
    }, [pendingDrop]);

    // ── Guide drag/move ───────────────────────────────────────────────────────
    const handleGuideDragStart = useCallback((guide, containerId) => {
        dragStateRef.current = { ...dragStateRef.current, draggingGuide: { guide, containerId } };
        startAutoScroll();
    }, [startAutoScroll]);

    const handleGuideDropIntoGuide = useCallback(async (guide, guideParent) => {
        if (!guide?.routeId || !guideParent) return;
        const guideId = String(guide.id);
        const targetParentId = guideParent.id ?? guideParent.Id;
        const previousForcedParentByGuideId = forcedParentByGuideId;
        const previousForcedRootGuideIds = forcedRootGuideIds;

        dragStateRef.current = { ...dragStateRef.current, draggingGuide: null };
        stopAutoScroll();
        setDragVisualResetTick((tick) => tick + 1);
        setForcedParentByGuideId((prev) => ({
            ...prev,
            [guideId]: targetParentId,
        }));
        setForcedRootGuideIds((prev) => prev.filter((id) => id !== guideId));

        const apiCallOptions = {
            method: "put",
            url: `/guide?routeId=${encodeURIComponent(guide.routeId)}`,
            data: {
                id: guide.id,
                parentId: targetParentId,
            },
        };

        try {
            await ApiCall(apiCallOptions, () => {});
        } catch {
            setForcedParentByGuideId(previousForcedParentByGuideId);
            setForcedRootGuideIds(previousForcedRootGuideIds);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    }, [ApiCall, forcedParentByGuideId, forcedRootGuideIds, stopAutoScroll, t]);

    const handleGuideDropToRoot = useCallback(async (guide) => {
        if (!guide?.routeId) return;
        const guideId = String(guide.id);
        const previousForcedRootGuideIds = forcedRootGuideIds;
        const previousForcedParentByGuideId = forcedParentByGuideId;

        dragStateRef.current = { ...dragStateRef.current, draggingGuide: null };
        stopAutoScroll();
        setDragVisualResetTick((tick) => tick + 1);
        setForcedRootGuideIds((prev) => (prev.includes(guideId) ? prev : [...prev, guideId]));
        setForcedParentByGuideId((prev) => {
            const next = { ...prev };
            delete next[guideId];
            return next;
        });

        const apiCallOptions = {
            method: "put",
            url: `/guide?routeId=${encodeURIComponent(guide.routeId)}`,
            data: {
                id: guide.id,
                parentId: null,
            },
        };

        try {
            await ApiCall(apiCallOptions, () => {});
        } catch {
            setForcedRootGuideIds(previousForcedRootGuideIds);
            setForcedParentByGuideId(previousForcedParentByGuideId);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    }, [ApiCall, forcedParentByGuideId, forcedRootGuideIds, stopAutoScroll, t]);

    const handleMoveGuide = useCallback((guide) => setMoveGuideTarget(guide), []);

    const handleConfirmMoveGuide = async (targetContainerId) => {
        const { routeId } = moveGuideTarget;
        setMoveGuideTarget(null);
        await ApiCall(
            {
                method: 'put',
                url: `/guidecontainer/assign-guide?routeId=${encodeURIComponent(routeId)}`,
                data: { containerId: targetContainerId, order: 9999 },
            },
            () => { /*loadContainers();*/ triggerReload(); }
        );
    };

    // ── Handlers — stable, drag callbacks only touch the ref ─────────────────
    const handlers = useMemo(() => ({
        dragStateRef,
        canDragContainers,
        onDeleteGuide:     handleDeleteGuide,
        onEditGuide:       handleOpenEditGuide,
        onNewArticle:      handleOpenNewArticle,
        onEditArticle:     handleOpenEditArticle,
        onMoveGuide:       handleMoveGuide,
        onNewContainer:    handleOpenNewContainer,
        onEditContainer:   handleOpenEditContainer,
        onDeleteContainer: handleDeleteContainer,
        onArticleDragStart: (article, routeId) => {
            dragStateRef.current = { ...dragStateRef.current, draggingArticle: { article, fromRouteId: routeId } };
            startAutoScroll();
        },
        onArticleDragEnd: () => {
            dragStateRef.current = { ...dragStateRef.current, draggingArticle: null };
            stopAutoScroll();
        },
        onArticleDropFromOutside: handleArticleDropFromOutside,
        onGuideDragStart:     handleGuideDragStart,
        onGuideDropIntoGuide: handleGuideDropIntoGuide,
        onGuideDropToRoot:    handleGuideDropToRoot,
        onGuideDragEnd:       handleGuideDragEnd,
        onGuideDrop:          handleGuideDrop,
        onGuideTouchStart:    handleGuideTouchStart,
        onGuideTouchMove:     handleGuideTouchMove,
        onGuideTouchEnd:      handleGuideTouchEnd,
        touchDragging,
        touchDropTarget,
        onContainerDragStart: handleContainerDragStart,
        onContainerDragEnd:   handleContainerDragEnd,
        onContainerDropInto:  handleContainerDropInto,
        onContainerTouchStart: handleContainerTouchStart,
        onContainerTouchMove:  handleContainerTouchMove,
        onContainerTouchEnd:   handleContainerTouchEnd,
        onReorderLevel: handleReorderLevel,
        childOrderOverrideByParent,
        articleCountByRoute,
        articleRefreshByRoute,
        dragVisualResetTick,
        sectionExpandedMap: expandedSections,
    }), [
        handleDeleteGuide, handleOpenEditGuide, handleOpenNewArticle, handleOpenEditArticle, handleMoveGuide,
        handleOpenNewContainer, handleOpenEditContainer, handleDeleteContainer,
        handleArticleDropFromOutside, handleGuideDragStart, handleGuideDropIntoGuide, handleGuideDropToRoot, handleGuideDragEnd,
        handleGuideDrop, handleGuideTouchStart, handleGuideTouchMove, handleGuideTouchEnd,
        handleContainerDragStart, handleContainerDragEnd, handleContainerDropInto,
        handleContainerTouchStart, handleContainerTouchMove, handleContainerTouchEnd,
        handleReorderLevel,
        childOrderOverrideByParent,
        articleCountByRoute,
        articleRefreshByRoute,
        dragVisualResetTick,
        touchDragging, touchDropTarget, expandedSections,
        canDragContainers,
        startAutoScroll, stopAutoScroll,
    ]);

    // ── renderGuideTree ───────────────────────────────────────────────────────
    const renderGuideTree = useCallback((items) => {
        //if (!containersReady) return <div style={{ textAlign: 'center', padding: 24 }}><Spinner color="success" /></div>;

        const effectiveGuides = mergeGuidesForRender(items);
        const baseTree = buildFilteredTree(containers, effectiveGuides, forcedRootGuideIds, forcedParentByGuideId);
        const tree = reorderNodesByIds(baseTree, rootOrderOverrideIds);
        treeRef.current = tree;

        return (
            <>
                {pendingDrop && (
                    <MoveOrCopyModal
                        article={pendingDrop.article}
                        onMove={handleConfirmMove}
                        onCopy={handleConfirmCopy}
                        onClose={() => setPendingDrop(null)}
                    />
                )}
                {moveGuideTarget && (
                    <MoveGuideModal
                        guide={moveGuideTarget}
                        containers={containers}
                        onMove={handleConfirmMoveGuide}
                        onClose={() => setMoveGuideTarget(null)}
                    />
                )}
                <div>
                    {tree.length === 0 ? (
                        <div className="gc-articles-empty">
                            <i className="bx bx-book-open" />
                            <span>{t('guide.noGuides') || 'No hay guías todavía.'}</span>
                        </div>
                    ) : (
                        renderRootNodes(
                            tree,
                            canEdit,
                            canDelete,
                            handlers,
                            handleReorderLevel,
                            treeRef,
                            dragStateRef,
                            expandedSections,
                            handleToggleSection,
                            expandedSubsections,
                            handleToggleSubsection,
                        )
                    )}
                </div>
            </>
        );
    }, [
        //containersReady,
        containers,
        pendingDrop,
        moveGuideTarget,
        handlers,
        handleReorderLevel,
        canEdit,
        canDelete,
        handleConfirmMove,
        handleConfirmCopy,
        expandedSections,
        expandedSubsections,
        handleToggleSection,
        handleToggleSubsection,
        forcedParentByGuideId,
        forcedRootGuideIds,
        mergeGuidesForRender,
        rootOrderOverrideIds,
        t,
    ]);

    const listOptions = useMemo(() => ({
        model: GuideModel,
        columns: ["routeId", "description"],
        actions: {},
        exportIsAvailable: true,
        urlGetListItemsToExport: "/guide/list-all",
        nameFile: t('guide.guides', 'Guides'),
        ignoreFields: ["routeId", "description"],
        filterTestIsAvailable: true,
        urlGetSearch: "/guide/list-all",
        viewName: "AdminGuide",
        reloadKey,
        layoutOptions: {
            wrapTopInCard: true,
            topSection: {
                renderHeader: () => (
                    <Row className="align-items-center">
                        <Col lg={6}>
                            <h2 className="ps-1 rokkitt-font">{t('guide.guides', 'Guides')}</h2>
                        </Col>
                        <Col lg={6} className="text-end">
                            {canEdit && canDragContainers && (
                                <Button className="box-green border-0" onClick={handleOpenNewGuide}>
                                    <i className="bx bx-plus pe-1" />
                                    {t('guide.newGuide') || 'New guide'}
                                </Button>
                            )}
                        </Col>
                    </Row>
                ),
            },
            table: {
                renderTable: (items) => renderGuideTree(items),
            },
        },
        pagination: {
            controlsView: "none",
            initialPage: 1,
            initialItemsPerPage: 10000,
            viewDropdownPerPage: false,
        }
    }), [canEdit, handleOpenNewGuide, reloadKey, renderGuideTree, t]);

    return (
        <Col lg="12" className="mb-5 mx-0 px-0">

            <CustomModal
                isOpen={displayDeleteModal}
                toggle={() => setDisplayDeleteModal(false)}
                body={
                    <div>
                        {t('validationMessage.modalDelete')}
                        {selectedGuide?.routeId && (
                            <p className="mt-2 text-muted"><strong>{t('guide.routeId')}:</strong> {selectedGuide.routeId}</p>
                        )}
                    </div>
                }
                footer={
                    <>
                        <Button className="box-green border-0" onClick={handleModalDelete}>{t('button.delete')}</Button>
                        <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>{t('button.cancel')}</Button>
                    </>
                }
            />

            <CustomModal
                isOpen={displayDeleteContainerModal}
                toggle={() => setDisplayDeleteContainerModal(false)}
                body={
                    <div>
                        {t('guide.confirmDeleteContainer') || 'Are you sure? Its guides will be moved to root.'}
                        {selectedContainer && <p className="mt-2 text-muted"><strong>{selectedContainer.name}</strong></p>}
                    </div>
                }
                footer={
                    <>
                        <Button className="box-green border-0" onClick={handleModalDeleteContainer}>{t('button.delete')}</Button>
                        <Button className="box-red border-0" onClick={() => setDisplayDeleteContainerModal(false)}>{t('button.cancel')}</Button>
                    </>
                }
            />

            <CustomModal
                isOpen={displayDeleteArticleFormModal}
                toggle={() => setDisplayDeleteArticleFormModal(false)}
                body={
                    <div>
                        {t('validationMessage.modalDelete')}
                        {articleFormData?.title && (
                            <p className="mt-2 text-muted"><strong>{articleFormData.title}</strong></p>
                        )}
                    </div>
                }
                footer={
                    <>
                        <Button className="box-green border-0" onClick={handleModalDeleteArticleFromForm}>{t('button.delete')}</Button>
                        <Button className="box-red border-0" onClick={() => setDisplayDeleteArticleFormModal(false)}>{t('button.cancel')}</Button>
                    </>
                }
            />

            <SlidePanel isOpen={articleFormOpen} onClose={handleCloseArticleForm} title={articleFormData ? t('button.edit') : (t('guide.newGuide') || 'Ny artikel')} variant="center" zIndexOffset={20}>
                <ArticleForm
                    initial={articleFormData ?? {}}
                    totalArticles={articleFormTotal}
                    onSave={handleSaveArticle}
                    onCancel={handleCloseArticleForm}
                    saving={articleFormSaving}
                    onDelete={handleAskDeleteArticleFromForm}
                    canDelete={canEdit}
                    isEdit={!!articleFormData}
                />
            </SlidePanel>

            <SlidePanel
                isOpen={guideFormOpen}
                onClose={handleCloseGuideForm}
                title={guideFormData ? (t('button.edit') || 'Edit') : (t('guide.newGuide') || 'New guide')}
                variant="center"
                zIndexOffset={20}
            >
                <GuideForm
                    initial={guideFormData ?? {}}
                    onSave={handleSaveGuide}
                    onCancel={handleCloseGuideForm}
                    onDelete={() => {
                        if (guideFormData) {
                            setSelectedGuide(guideFormData);
                            setDisplayDeleteModal(true);
                        }
                    }}
                    saving={guideFormSaving}
                    isEdit={!!guideFormData}
                    canDelete={canDelete && canDragContainers}
                />
            </SlidePanel>

            <SlidePanel isOpen={containerFormOpen} onClose={handleCloseContainerForm} title={containerFormData ? t('button.edit') : (t('guide.newContainer'))} variant="center" zIndexOffset={20}>
                <ContainerForm initial={containerFormData ?? { parentId: containerFormParent }} onSave={handleSaveContainer} onCancel={handleCloseContainerForm} saving={containerFormSaving} />
            </SlidePanel>

            <SortableList key={reloadKey} listOptions={listOptions} />
        </Col>
    );
};

export default AdminGuideList;